import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, FileText, Lock, MessageSquare } from "lucide-react";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { PORTAL_URL, usePageTitle } from "@/lib/site";

const FEATURES = [
  {
    icon: FileText,
    title: "Share documents securely",
    desc: "Upload statements, workpapers, and source documents through encrypted file exchange.",
  },
  {
    icon: MessageSquare,
    title: "Message your team",
    desc: "Keep questions and answers in one organized, private thread — no lost emails.",
  },
  {
    icon: Lock,
    title: "Review deliverables",
    desc: "Access reports, reconciliations, and audit packages whenever you need them.",
  },
];

export default function Portal() {
  usePageTitle("Client Portal — CalCor Solutions");

  return (
    <>
      <section data-testid="portal-hero" className="pt-36 pb-20 sm:pt-44 lg:pb-28">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <MaskedLine delay={0.15} className="text-xs font-semibold uppercase tracking-[0.25em] text-pine">
            Client Portal
          </MaskedLine>
          <h1 className="mt-6 font-serif text-5xl font-medium leading-[1.05] tracking-tighter text-ink sm:text-6xl">
            <MaskedLine delay={0.3}>Your work,</MaskedLine>
            <MaskedLine delay={0.45}>
              <em className="text-pine">always within reach.</em>
            </MaskedLine>
          </h1>
          <FadeUp delay={0.6}>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-600 sm:text-lg">
              The CalCor client portal is the secure home for your engagement — documents,
              deliverables, and conversations in one place. Access credentials are provided
              during onboarding.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="portal-open-button"
                className="group inline-flex items-center gap-2 rounded-full bg-pine px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
              >
                <Lock className="h-4 w-4" />
                Go to the client portal
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <p className="text-xs text-zinc-500">
                Opens the secure Financial Cents portal — sign in with the credentials provided
                during onboarding.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section data-testid="portal-features" className="border-t border-black/10 bg-white py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl gap-px overflow-hidden px-6 sm:grid-cols-3 lg:rounded-3xl lg:border lg:border-black/10 lg:bg-black/10 lg:px-0">
          {FEATURES.map((f, i) => (
            <FadeUp key={f.title} delay={i * 0.1} className="bg-white">
              <div data-testid={`portal-feature-${i + 1}`} className="h-full p-9 transition-colors duration-300 hover:bg-paper">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-pine/10">
                  <f.icon className="h-5 w-5 text-pine" />
                </span>
                <h3 className="mt-5 font-serif text-xl tracking-tight text-ink">{f.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-600">{f.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section data-testid="portal-cta" className="py-20 lg:py-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <FadeUp>
            <div className="rounded-3xl border border-black/10 bg-white p-10 sm:p-12">
              <h2 className="font-serif text-3xl tracking-tight text-ink">Not a client yet?</h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-zinc-600 sm:text-base">
                Portal access comes with every engagement. Start with a consultation and we will
                walk you through how working together feels — tools included.
              </p>
              <Link
                to="/contact"
                data-testid="portal-cta-contact-button"
                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-pine px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-clay"
              >
                Request a consultation
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
